// eslint-disable-next-line no-unused-vars
import React, { memo, useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';

import useFetch from 'Hooks/useFetch';
import useRouter from 'Hooks/useRouter';
import useUser from 'Hooks/useUser';
import { API_ACCOUNT_ACTIVATE_USER } from 'Constants';
import ActivateUserModalComponent from 'Components/ActivateUser/ActivateUserModal';

const languages = [
  { label: 'RO', value: 'ro' },
  { label: 'EN', value: 'en' }
];

const defaultValues = {
  email: '',
  password: '',
  confirmPassword: '',
  operationalConsent: null,
  marketingConsent: null,
};

const ActivateUserModal = memo(() => {
  const [profile, setProfile] = useState({});
  const [profileLoading, setProfileLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [userActivated, setUserActivated] = useState(false);
  const [errors, setErrors] = useState([]);
  const [consentMessages, setConsentMessages] = useState({});
  const [operationalNotification, setOperationalNotification] = useState([]);
  const [marketingNotification, setMarketingNotification] = useState([]);
  const { data: user, setUserData } = useUser();
  const { params, push } = useRouter();
  const modalRef = useRef(null);

  const { t } = useTranslation();
  const fetcher = useFetch();
  const {
    formState: { errors: rhfErrors },
    getValues,
    setValue,
    register,
    watch,
    reset,
    trigger,
  } = useForm({
    defaultValues,
    mode: 'onChange',
  });

  const passwordValue = watch('password');
  const confirmPasswordValue = watch('confirmPassword');
  const operationalConsentValue = watch('operationalConsent');
  const marketingConsentValue = watch('marketingConsent');
  const required = t('errors.fieldBlank');
  const minLength = value => ({ value, message: t('errors.fieldMinLength', { minLength: value }) });
  const maxLength = value => ({ value, message: t('errors.fieldMaxLength', { maxLength: value }) });
  const pattern = value => ({ value, message: t('errors.passwordInvalidFormat') });

  const validators = useMemo(() => ({
    email: { required },
    password: {
      required,
      minLength: minLength(8),
      maxLength: maxLength(50),
      pattern: pattern(/[^\w\d]*(([0-9]+.*[A-Za-z]+.*)|[A-Za-z]+.*([0-9]+.*))/),
    },
    confirmPassword: {
      required,
      minLength: minLength(8),
      maxLength: maxLength(50),
      pattern: pattern(/[^\w\d]*(([0-9]+.*[A-Za-z]+.*)|[A-Za-z]+.*([0-9]+.*))/),
      validate: value => (value !== (passwordValue || '')) ? t('errors.passwordsDoNotMatch') : true,
    },
  }), [passwordValue]);

  useEffect(() => {
    getAccount();
  }, []);

  const getAccount = async () => {
    setErrors([]);
    setProfileLoading(true);

    fetcher(`${API_ACCOUNT_ACTIVATE_USER}/${ params.id }`).then(res => {
      setProfile({
        userId: res?.user?.userId,
      });
      reset({
        email: res?.user?.email || '',
        operationalConsent: res?.blocks?.operationalConsent || false,
        marketingConsent: res?.blocks?.marketingConsent || false,
      });
      setConsentMessages(res?.blocks);
    }).catch(err => setErrors([{ message: err.info.map(i => i?.message).join('\r\n'), type: 'error' }]))
      .finally(() => setProfileLoading(false));
  };

  // Set consent notifications
  useEffect(() => {
    if (Object.keys(consentMessages || {}).length) {
      if (operationalConsentValue === false) {
        setOperationalNotification([{ message: consentMessages?.gdpr_form_aggreement_operational_not_checked_hint, type: 'error' }]);
      } else if (operationalConsentValue === true) {
        setOperationalNotification([{ message: consentMessages?.gdpr_form_aggreement_operational_checked_hint, type: 'success' }]);
      } else {
        setOperationalNotification([]);
      }

      if (marketingConsentValue === false) {
        setMarketingNotification([{ message: consentMessages?.gdpr_form_aggreement_marketing_not_checked_hint, type: 'error' }]);
      } else if (marketingConsentValue === true) {
        setMarketingNotification([{ message: consentMessages?.gdpr_form_aggreement_marketing_checked_hint, type: 'success' }]);
      } else {
        setMarketingNotification([]);
      }
    }
  }, [marketingConsentValue, operationalConsentValue, consentMessages]);

  const handleConsent = (type, value) => {
    setValue(type, value);
    if (type === 'operationalConsent' && value === false) {
      if (marketingConsentValue === true) {
        setValue('marketingConsent', false);
      }
    }
  };

  const handleSubmit = async () => {
    await trigger();

    const rhfErrorsKeys = Object.keys(rhfErrors);
    if (rhfErrorsKeys.length || passwordValue === '' || confirmPasswordValue === '') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    setLoading(true);

    const {
      email,
      password,
      confirmPassword,
      operationalConsent,
      marketingConsent,
    } = getValues();

    const parsedData = {
      email,
      ...profile,
      newPassword: { first: password, second: confirmPassword },
      consent: {
        operationalConsent,
        marketingConsent,
      },
    };

    fetcher(`${API_ACCOUNT_ACTIVATE_USER}/${ params.id }`, {
      headers: new Headers({ 'Accept-Language': user?.language }),
      method: 'POST',
      body: JSON.stringify(parsedData),
    }).then(() => {
      setUserActivated(true);
      setTimeout(() => {
        push('/login');
      }, 5000);
    }).catch(err => {
      modalRef.current.scrollIntoView({ behavior: 'smooth' });
      setErrors([{ message: err?.info?.errors[0]?.message || t('errors.genericErrorMessage'), type: 'error' }]);
    }).finally(() => setLoading(false));
  };

  return (
    <ActivateUserModalComponent
      register={ register }
      loading={ loading }
      profileLoading={ profileLoading }
      onSubmit={ handleSubmit }
      errors={ errors }
      rhfErrors={ rhfErrors }
      languages={ languages }
      language={ languages.find(lang => lang.value === user?.language) }
      setLanguage={ lang => setUserData({ language: lang.value }) }
      validators={ validators }
      userActivated={ userActivated }
      handleConsent={ handleConsent }
      operationalNotification={ operationalNotification }
      marketingNotification={ marketingNotification }
      operationalConsentValue={ operationalConsentValue }
      ref={ modalRef }
    />
  );
});

ActivateUserModal.displayName = 'ActivateUserModal';

export default ActivateUserModal;
